import { createTheme } from '@mui/material/styles';

/**
 * 메디브릿지 디자인 시스템
 * 
 * 병원 포털과 간호사 앱 간의 디자인 일관성을 위해 설계된 테마입니다.
 * MUI의 ThemeProvider를 통해 전역으로 적용됩니다.
 */
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2', // 메디컬 블루 - 메인 브랜드 컬러
      light: '#42a5f5', // 라이트 블루 - 보조 컬러
      dark: '#1565c0',
      contrastText: '#fff',
    },
    secondary: {
      main: '#42a5f5', // 라이트 블루 - 보조 컬러
      light: '#80d6ff',
      dark: '#0077c2',
      contrastText: '#fff',
    },
    success: {
      main: '#4caf50', // 그린 - 액션 버튼 컬러
      light: '#81c784',
      dark: '#388e3c',
    },
    warning: {
      main: '#ff9800',
      light: '#ffb74d',
      dark: '#f57c00',
    },
    error: {
      main: '#f44336',
      light: '#e57373',
      dark: '#d32f2f',
    },
    info: {
      main: '#2196f3',
      light: '#64b5f6',
      dark: '#1976d2',
    },
    background: {
      default: '#f5f7fa', // 밝은 배경색
      paper: '#ffffff', // 카드와 컨테이너 배경
    },
    text: {
      primary: '#172b4d', // 진한 텍스트
      secondary: '#5e6c84', // 보조 텍스트
      disabled: '#A9B9C9', // 비활성화된 텍스트
    },
  },
  typography: {
    fontFamily: 'Noto Sans KR, Roboto, sans-serif',
    // 폰트 사이즈 증가로 가독성 향상
    h1: {
      fontSize: '28px',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h2: {
      fontSize: '24px',
      fontWeight: 700,
      lineHeight: 1.4,
    },
    h3: {
      fontSize: '20px',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    h4: {
      fontSize: '18px',
      fontWeight: 500,
      lineHeight: 1.4,
    },
    subtitle1: {
      fontSize: '18px',
      fontWeight: 500,
    },
    subtitle2: {
      fontSize: '16px',
      fontWeight: 500,
    },
    body1: {
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: 1.6,
    },
    caption: {
      fontSize: '12px',
      fontWeight: 400,
      lineHeight: 1.4,
    },
    button: {
      textTransform: 'none', // 버튼 텍스트를 대문자로 변환하지 않음
      fontWeight: 500,
      fontSize: '16px',
    },
  },
  spacing: 8, // 기본 간격 단위 8px
  shape: {
    borderRadius: 8, // 중간 크기 border radius
  },
  shadows: [
    'none',
    '0 2px 4px rgba(0,0,0,0.05)', // 기본 그림자
    '0 4px 8px rgba(0,0,0,0.1)', // 중간 그림자
    '0 8px 16px rgba(0,0,0,0.15)', // 강한 그림자
    '0 8px 16px rgba(0,0,0,0.15)', // 추가 그림자 레벨
    '0 10px 20px rgba(0,0,0,0.15)',
    '0 12px 24px rgba(0,0,0,0.18)',
    '0 14px 28px rgba(0,0,0,0.20)',
    '0 16px 32px rgba(0,0,0,0.22)',
    '0 18px 36px rgba(0,0,0,0.25)',
    '0 20px 40px rgba(0,0,0,0.30)',
    '0 22px 44px rgba(0,0,0,0.35)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
    '0 24px 48px rgba(0,0,0,0.40)',
  ],
  components: {
    // 버튼 컴포넌트 스타일 개선
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '10px 24px', // 더 큰 버튼 패딩
          borderRadius: '8px', // 일관된 라운딩
          fontWeight: 500,
        },
        contained: {
          boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
          '&:hover': {
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          },
        },
      },
    },
    // 카드 컴포넌트 스타일 개선
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '12px', // 더 둥근 카드
          boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
          overflow: 'visible',
        },
      },
    },
    // 입력 필드 스타일 개선
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px', // 일관된 라운딩
            '& fieldset': {
              borderColor: 'rgba(0, 0, 0, 0.12)',
            },
            '&:hover fieldset': {
              borderColor: '#1976d2',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#1976d2',
            },
          },
          '& .MuiInputLabel-root': {
            fontSize: '14px',
          },
          '& .MuiOutlinedInput-input': {
            padding: '14px 16px', // 더 편안한 패딩
          },
        },
      },
    },
    // 테이블 스타일 개선
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: '16px', // 더 넓은 셀 패딩
          fontSize: '14px', // 더 큰 폰트 사이즈
        },
        head: {
          fontWeight: 600,
          backgroundColor: '#f5f7fa',
        },
      },
    },
  },
});

export default theme; 